import { ReactElement, useMemo } from 'react';
import { Box, List, ListItem, ListItemText } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { MapDto } from '../../../models';
import { MapSearchSuggestion, MapSearchResult } from '../../../redux';
import { getSearchHistory } from '../../../services';
import { Body2, Header6, LoadingContainer } from '../../atoms';
import { MapCardContainer } from '../../molecules';
import { useSearch } from '../../../hooks/useSearch';

import './search-field-dropdown.styles.scss';
import { useTranslation } from 'react-i18next';

export interface ISearchFieldDropDownProps {
	onClick: () => void;
}

export function SearchFieldDropdown(
	props: ISearchFieldDropDownProps
): ReactElement {
	const { onClick } = props;
	const { compactDisplay, suggestions, searchValue, search } = useSearch();
	const history = useHistory();
	const { t } = useTranslation('search');

	const previews: MapSearchResult[] = useMemo<MapSearchResult[]>(() => {
		if (!compactDisplay) return suggestions.resultPreviews;
		return suggestions.resultPreviews.slice(0, 2);
	}, [suggestions.resultPreviews]);

	const searchSuggestions: MapSearchSuggestion[] =
		suggestions.suggestions.slice(0, 4);

	function hasSuggestions(): boolean {
		return hasSearchSuggestions() || hasPreviews();
	}

	function hasSearchSuggestions(): boolean {
		return suggestions.suggestions.length > 0;
	}

	function hasPreviews(): boolean {
		return suggestions.resultPreviews.length > 0;
	}

	function getHightlight(suggestion: string): string {
		return suggestion.substring(0, getCutoffIndex(suggestion));
	}

	function getCutoffIndex(suggestion: string): number {
		let searchIndex: number = 0;
		let suggestionIndex: number = 0;
		while (
			searchIndex < searchValue.length &&
			suggestionIndex < suggestion.length
		) {
			if (
				searchValue.toLowerCase().charAt(searchIndex) ===
				suggestion.toLowerCase().charAt(suggestionIndex)
			) {
				searchIndex++;
			}
			suggestionIndex++;
		}
		return suggestionIndex;
	}

	function subtractSearchValue(suggestion: string): string {
		return suggestion.substring(
			getCutoffIndex(suggestion),
			suggestion.length
		);
	}

	function navigateToMap(map: MapSearchResult): void {
		let queryString: string = '';

		if (map.topoLat && map.topoLng && map.topoName) {
			queryString = `?lat=${map.topoLat}&lng=${
				map.topoLng
			}&name=${map.topoName.capitalize()}`;
		}

		history.push(`/map/${map.id}${queryString}`);
		if (onClick) onClick();
	}

	function SearchHistory(): ReactElement {
		return (
			<Box>
				<Header6 className="list-title">{t('prevSearched')}</Header6>
				<List>
					{getSearchHistory().map(
						(searchString: string, index: number) => (
							<ListItem
								className="list-item"
								onClick={() => search(searchString)}
								key={index}
								disableGutters
								button
							>
								<ListItemText primary={searchString} />
							</ListItem>
						)
					)}
				</List>
			</Box>
		);
	}

	function SearchPreviews(): ReactElement {
		return (
			<Box>
				<Header6 className="list-title">{t('mapSuggestions')}</Header6>
				<MapCardContainer
					big
					className="search-suggestions-preview"
					columns={compactDisplay ? 2 : 3}
					maps={previews}
					onClick={(map: MapDto | MapSearchResult) =>
						navigateToMap(map as MapSearchResult)
					}
				/>
			</Box>
		);
	}

	function EmptySuggestions(): ReactElement {
		return (
			<Body2 className="empty-suggestions-body">
				{t('noResultsFound')}
			</Body2>
		);
	}

	function SearchSuggestions(): ReactElement {
		return (
			<Box>
				<Header6 className="list-title">{t('searchTips')}</Header6>
				<List>
					{searchSuggestions.map(
						(suggestion: MapSearchSuggestion, index: number) => (
							<ListItem
								onClick={() => search(suggestion.suggestion)}
								className=" list-item suggestion-list-item"
								key={index}
								disableGutters
								button
								data-mdat-item-id="search-suggestion-item"
							>
								<ListItemText>
									<b>
										{getHightlight(suggestion.suggestion)}
									</b>
									{subtractSearchValue(suggestion.suggestion)}
								</ListItemText>
								<ListItemText
									secondary={t('amountMaps', {
										amount: suggestion.count
									})}
								/>
							</ListItem>
						)
					)}
				</List>
			</Box>
		);
	}

	return (
		<>
			<LoadingContainer sections={['search-suggestions']}>
				<Box className="search-field-dropdown">
					{searchValue.length === 0 && <SearchHistory />}
					{!hasSuggestions() && searchValue.length > 0 && (
						<EmptySuggestions />
					)}
					{hasSuggestions() && (
						<>
							{hasPreviews() && <SearchPreviews />}
							{hasSearchSuggestions() && <SearchSuggestions />}
						</>
					)}
				</Box>
				{hasSuggestions() && (
					<List disablePadding>
						<ListItem
							className="show-all-results-btn"
							onClick={() => search(searchValue)}
							button
						>
							<ListItemText color="primary">
								{t('viewSearchResults')}
							</ListItemText>
						</ListItem>
					</List>
				)}
			</LoadingContainer>
		</>
	);
}
